import { useEffect, useRef } from "react";
import * as d3 from "d3";
import type { MonthlySeries, PortfolioHomeData } from "./types";

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(v: number | null | undefined, decimals = 0): string {
  if (v == null) return "—";
  return v.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fmtDelta(days: number | null | undefined): {
  label: string;
  positive: boolean;
} {
  if (days == null) return { label: "—", positive: false };
  const abs = Math.abs(days);
  const sign = days > 0 ? "+" : days < 0 ? "−" : "";
  return { label: `${sign}${abs}d`, positive: days <= 0 };
}

function monthIdToDate(monthId: number): Date {
  const y = Math.floor(monthId / 100);
  const m = (monthId % 100) - 1;
  return new Date(y, m, 1);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-widest text-[#8C9BAB] mb-3">
      {children}
    </h3>
  );
}

type KpiTileProps = {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
};

function KpiTile({ label, value, sub, accent }: KpiTileProps) {
  return (
    <div className="flex flex-col gap-1 rounded-[8px] border border-[#F0F2F4] bg-white px-5 py-4 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
      <span className="text-[11px] font-medium text-[#8C9BAB] uppercase tracking-wide">
        {label}
      </span>
      <span
        className="text-[24px] font-bold leading-none tracking-tight"
        style={{ color: accent ?? "#1F2933" }}
      >
        {value}
      </span>
      {sub && <span className="text-[12px] text-[#8C9BAB]">{sub}</span>}
    </div>
  );
}

type MilestoneTileProps = {
  label: string;
  planned: string | null | undefined;
  actual: string | null | undefined;
  deltaDays: number | null | undefined;
};

function MilestoneTile({
  label,
  planned,
  actual,
  deltaDays,
}: MilestoneTileProps) {
  const hasData = planned || actual;
  const delta = fmtDelta(deltaDays);

  return (
    <div className="flex flex-col gap-2 rounded-[8px] border border-[#F0F2F4] bg-white px-5 py-4 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-[#8C9BAB]">
        {label}
      </span>
      {!hasData ? (
        <span className="text-[14px] text-[#C0C7D0]">No date available</span>
      ) : (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-[#8C9BAB]">Planned</span>
            <span className="text-[13px] font-medium text-[#48636F]">
              {fmtDate(planned)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-[#8C9BAB]">Actual</span>
            <span className="text-[13px] font-semibold text-[#1F2933]">
              {fmtDate(actual)}
            </span>
          </div>
          {deltaDays != null && (
            <div className="mt-1 flex items-center justify-between border-t border-[#F0F2F4] pt-1">
              <span className="text-[11px] text-[#8C9BAB]">Variance</span>
              <span
                className="text-[12px] font-semibold"
                style={{ color: delta.positive ? "#27AE60" : "#E74C3C" }}
              >
                {delta.label}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Enrollment chart ──────────────────────────────────────────────────────────

const CHART_COLOR = "#06709B";
const MARGIN = { top: 16, right: 24, bottom: 36, left: 52 };
const HEIGHT = 240;

function EnrollmentChart({
  series,
  plannedSubjects,
}: {
  series: MonthlySeries[];
  plannedSubjects: number | null;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!series.length || !svgRef.current || !containerRef.current) return;

    const containerWidth = containerRef.current.clientWidth || 700;
    const w = containerWidth - MARGIN.left - MARGIN.right;
    const h = HEIGHT - MARGIN.top - MARGIN.bottom;

    const xDates = series.map((p) => monthIdToDate(p.month_id));
    const yMax = Math.max(
      d3.max(series, (p) => p.cumulative_actual) ?? 0,
      plannedSubjects ?? 0,
    );

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", containerWidth).attr("height", HEIGHT);

    const g = svg
      .append("g")
      .attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);

    const xScale = d3
      .scaleTime()
      .domain([d3.min(xDates)!, d3.max(xDates)!])
      .range([0, w]);

    const yScale = d3
      .scaleLinear()
      .domain([0, yMax * 1.08])
      .nice()
      .range([h, 0]);

    // Grid
    g.append("g")
      .call(
        d3
          .axisLeft(yScale)
          .tickSize(-w)
          .tickFormat(() => ""),
      )
      .selectAll("line")
      .attr("stroke", "#F0F2F4")
      .attr("stroke-dasharray", "3,3");
    g.select(".domain").remove();

    // X axis
    g.append("g")
      .attr("transform", `translate(0,${h})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(d3.timeYear.every(1))
          .tickFormat((d) => d3.timeFormat("%Y")(d as Date)),
      )
      .selectAll("text")
      .attr("fill", "#6B7280")
      .style("font-size", "11px");

    g.append("g")
      .call(d3.axisLeft(yScale).ticks(5))
      .selectAll("text")
      .attr("fill", "#6B7280")
      .style("font-size", "11px");

    // Target line
    if (plannedSubjects && plannedSubjects > 0) {
      const xEnd = xScale(d3.max(xDates)!);
      g.append("line")
        .attr("x1", 0)
        .attr("x2", xEnd)
        .attr("y1", yScale(plannedSubjects))
        .attr("y2", yScale(plannedSubjects))
        .attr("stroke", CHART_COLOR)
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "6,4")
        .attr("opacity", 0.4);
      g.append("text")
        .attr("x", xEnd + 4)
        .attr("y", yScale(plannedSubjects) + 4)
        .attr("fill", CHART_COLOR)
        .style("font-size", "10px")
        .text(`Target: ${plannedSubjects.toLocaleString()}`);
    }

    // Enrollment line
    const lineGen = d3
      .line<MonthlySeries>()
      .x((d) => xScale(monthIdToDate(d.month_id)))
      .y((d) => yScale(d.cumulative_actual))
      .curve(d3.curveMonotoneX);

    g.append("path")
      .datum(series)
      .attr("fill", "none")
      .attr("stroke", CHART_COLOR)
      .attr("stroke-width", 2.5)
      .attr("d", lineGen);

    // End dot
    const last = series[series.length - 1];
    g.append("circle")
      .attr("cx", xScale(monthIdToDate(last.month_id)))
      .attr("cy", yScale(last.cumulative_actual))
      .attr("r", 4)
      .attr("fill", CHART_COLOR);
  }, [series, plannedSubjects]);

  return (
    <div ref={containerRef} className="w-full">
      <svg ref={svgRef} className="w-full overflow-visible" />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function PortfolioTrialHomeScreen({
  data,
}: {
  data: PortfolioHomeData;
}) {
  const { milestones, kpis, monthly_series } = data;

  const hasMilestoneData = milestones.items.length > 0;

  const enrolledPct =
    kpis.pct_enrolled != null ? fmt(kpis.pct_enrolled, 1) + "%" : "—";
  const enrolledSub =
    kpis.enrolled != null && kpis.planned_subjects != null
      ? `${fmt(kpis.enrolled)} of ${fmt(kpis.planned_subjects)} planned`
      : kpis.enrolled != null
        ? `${fmt(kpis.enrolled)} enrolled`
        : undefined;

  const sitesSub =
    kpis.sites_planned && kpis.sites_planned > 0
      ? `of ${fmt(kpis.sites_planned)} planned`
      : undefined;

  return (
    <div className="flex flex-col gap-8">
      {/* KPI tiles */}
      <div>
        <SectionLabel>Key metrics</SectionLabel>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <KpiTile label="Enrolled" value={enrolledPct} sub={enrolledSub} />
          <KpiTile
            label="Sites active"
            value={fmt(kpis.sites_active)}
            sub={sitesSub}
          />
          <KpiTile label="Countries" value={fmt(kpis.countries)} />
          <KpiTile
            label="Recruitment rate"
            value={
              kpis.recruitment_rate != null
                ? fmt(kpis.recruitment_rate, 1)
                : "—"
            }
            sub="subjects / month"
          />
          <KpiTile
            label="Screen failure rate"
            value={
              kpis.screen_failure_rate != null
                ? fmt(kpis.screen_failure_rate, 1) + "%"
                : "—"
            }
          />
        </div>
      </div>

      {/* Enrollment curve */}
      {monthly_series && monthly_series.length > 1 && (
        <div>
          <div className="mb-3">
            <SectionLabel>Cumulative enrollment</SectionLabel>
          </div>
          <div className="rounded-[8px] border border-[#F0F2F4] bg-white p-5 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
            <div className="flex items-center gap-6 mb-4 text-[12px] text-[#6B7280]">
              <span>
                <span className="font-semibold text-[#1F2933]">
                  {fmt(
                    monthly_series[monthly_series.length - 1]
                      ?.cumulative_actual,
                  )}
                </span>{" "}
                enrolled
              </span>
              {kpis.planned_subjects && (
                <span>
                  <span className="font-semibold text-[#1F2933]">
                    {fmt(kpis.planned_subjects)}
                  </span>{" "}
                  target
                </span>
              )}
              {kpis.pct_enrolled != null && (
                <span>
                  <span
                    className="font-semibold"
                    style={{
                      color: kpis.pct_enrolled >= 100 ? "#2B9D5F" : "#1F2933",
                    }}
                  >
                    {fmt(kpis.pct_enrolled, 1)}%
                  </span>{" "}
                  to plan
                </span>
              )}
              <span className="ml-auto text-[11px] text-[#9CA3AF]">
                Solid = actual · Dashed = target
              </span>
            </div>
            <EnrollmentChart
              series={monthly_series}
              plannedSubjects={kpis.planned_subjects}
            />
          </div>
        </div>
      )}

      {/* Milestone intervals */}
      {(milestones.days_fsa_to_fsi != null ||
        milestones.days_fsi_to_lsi != null) && (
        <div>
          <SectionLabel>Enrollment timeline</SectionLabel>
          <div className="flex flex-wrap gap-4">
            {milestones.days_fsa_to_fsi != null && (
              <div className="flex flex-col gap-0.5 rounded-[8px] border border-[#F0F2F4] bg-white px-5 py-4 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#8C9BAB]">
                  First IRB Approval → First Patient In
                </span>
                <span className="text-[28px] font-bold tracking-tight text-[#1F2933]">
                  {fmt(milestones.days_fsa_to_fsi)}
                  <span className="ml-1 text-[14px] font-normal text-[#8C9BAB]">
                    days
                  </span>
                </span>
              </div>
            )}
            {milestones.days_fsi_to_lsi != null && (
              <div className="flex flex-col gap-0.5 rounded-[8px] border border-[#F0F2F4] bg-white px-5 py-4 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#8C9BAB]">
                  First subject in → Last subject in
                </span>
                <span className="text-[28px] font-bold tracking-tight text-[#1F2933]">
                  {fmt(milestones.days_fsi_to_lsi)}
                  <span className="ml-1 text-[14px] font-normal text-[#8C9BAB]">
                    days
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Milestone tiles */}
      <div>
        <SectionLabel>Milestones</SectionLabel>
        {!hasMilestoneData ? (
          <p className="text-[13px] text-[#B0BAC5]">
            No milestone data available for this trial.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {milestones.items.map((ms) => (
              <MilestoneTile
                key={ms.label}
                label={ms.label}
                planned={ms.planned}
                actual={ms.actual}
                deltaDays={ms.delta_days}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
